import { useEffect, useState } from "react";
import { Gauge, Loader2, Flag, Clock, Zap, Trophy, MapPin, Car, StopCircle, X, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { PageHeader } from "@/components/common";

export const fmtLap = (ms) => {
  if (!ms && ms !== 0) return "—";
  const m = Math.floor(ms / 60000);
  const s = ((ms % 60000) / 1000).toFixed(3);
  return `${m}:${s.padStart(6, "0")}`;
};

const fmtDur = (s) => {
  if (!s?.started_at) return "—";
  const end = s.ended_at ? new Date(s.ended_at) : new Date();
  const mins = Math.max(0, Math.round((end - new Date(s.started_at)) / 60000));
  return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
};

export default function MySessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = () => apiClient.get("/simhub/sessions/me").then(({ data }) => setSessions(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const endSession = async () => {
    try { await apiClient.post("/simhub/unpair"); toast.success("Session ended"); setActive(null); load(); }
    catch (err) { toast.error(getErrorMessage(err, "Failed")); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  return (
    <div>
      <PageHeader title="Sim Sessions" subtitle="Telemetry captured from SimHub while you drive" icon={Gauge} />
      {sessions.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center" data-testid="sessions-empty">
          <Gauge className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-semibold">No sessions yet</p>
          <p className="text-slate-500 text-sm mt-1">Scan a simulator&apos;s QR code to pair, then your lap data will appear here.</p>
        </div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden" data-testid="sessions-table">
          {/* Header */}
          <div className="hidden md:grid grid-cols-[1.4fr_1.3fr_1fr_0.8fr_0.9fr_0.7fr_0.7fr] gap-3 px-5 py-3 text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/10"
            style={{ background: "rgba(0,229,255,0.05)" }}>
            <span>Session</span><span>Track / Car</span><span>Best Lap</span><span>Laps</span><span>Top Speed</span><span>Points</span><span></span>
          </div>
          {sessions.map((s) => (
            <button key={s.id} onClick={() => setActive(s)} data-testid={`session-${s.id}`}
              className="w-full grid md:grid-cols-[1.4fr_1.3fr_1fr_0.8fr_0.9fr_0.7fr_0.7fr] grid-cols-2 gap-3 px-5 py-4 text-left border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors items-center">
              <div className="min-w-0">
                <p className="text-white font-semibold truncate flex items-center gap-2">
                  {s.status === "active" && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: "#00ff9d", boxShadow: "0 0 8px #00ff9d" }} />}
                  {s.station_name}
                </p>
                <p className="text-slate-500 text-xs">{new Date(s.started_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · {new Date(s.started_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</p>
              </div>
              <div className="min-w-0">
                <p className="text-slate-200 text-sm truncate flex items-center gap-1.5"><MapPin className="w-3 h-3 text-slate-500 shrink-0" />{s.track || "—"}</p>
                <p className="text-slate-500 text-xs truncate flex items-center gap-1.5"><Car className="w-3 h-3 shrink-0" />{s.car || "—"}</p>
              </div>
              <span className="font-display font-bold" style={{ color: "#00ff9d" }}>{fmtLap(s.best_lap_ms)}</span>
              <span className="text-slate-200 text-sm">{s.lap_count || 0}</span>
              <span className="text-slate-200 text-sm">{s.top_speed ? `${s.top_speed} km/h` : "—"}</span>
              <span className="font-display font-bold" style={{ color: "#ffb020" }}>+{s.points_awarded || 0}</span>
              <span className="hidden md:flex justify-end"><ChevronRight className="w-4 h-4 text-slate-600" /></span>
            </button>
          ))}
        </div>
      )}

      {active && <SessionModal s={active} onClose={() => setActive(null)} onEnd={endSession} />}
    </div>
  );
}

function SessionModal({ s, onClose, onEnd }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(2,4,10,0.75)", backdropFilter: "blur(6px)" }}
      onClick={onClose} data-testid="session-modal">
      <div className="glass rounded-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-white/10" style={{ background: "rgba(0,229,255,0.06)" }}>
          <div>
            <p className="text-white font-display font-bold text-xl">{s.station_name}</p>
            <p className="text-slate-500 text-xs mt-0.5">{new Date(s.started_at).toLocaleString()} · {fmtDur(s)}</p>
          </div>
          <div className="flex items-center gap-2">
            {s.status === "active"
              ? <span className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest text-black" style={{ background: "#00ff9d" }}>Live</span>
              : <span className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest text-slate-300 border border-white/10">Ended</span>}
            <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="session-modal-close"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 p-5">
          {s.discipline === "flight" ? (
            <>
              <Stat icon={Car} label="Aircraft" value={s.aircraft || s.car || "—"} />
              <Stat icon={Gauge} label="Top Speed" value={s.top_speed ? `${s.top_speed} km/h` : "—"} />
              <Stat icon={Flag} label="Max Altitude" value={s.max_altitude_ft ? `${Math.round(s.max_altitude_ft).toLocaleString()} ft` : "—"} color="#00e5ff" />
              <Stat icon={MapPin} label="Distance" value={s.distance_km ? `${s.distance_km} km` : "—"} />
              <Stat icon={MapPin} label="Route" value={(s.departure_airport || s.arrival_airport) ? `${s.departure_airport || "?"} → ${s.arrival_airport || "?"}` : "—"} />
              <Stat icon={Clock} label="Duration" value={fmtDur(s)} />
              <Stat icon={Zap} label="Points" value={`+${s.points_awarded || 0}`} color="#ffb020" />
            </>
          ) : (
            <>
              <Stat icon={MapPin} label="Track" value={s.track || "—"} />
              <Stat icon={Car} label="Car" value={s.car || "—"} />
              <Stat icon={Trophy} label="Best Lap" value={fmtLap(s.best_lap_ms)} color="#00ff9d" />
              <Stat icon={Clock} label="Last Lap" value={fmtLap(s.last_lap_ms)} />
              <Stat icon={Flag} label="Laps" value={`${s.lap_count || 0}${s.valid_lap_count != null ? ` (${s.valid_lap_count} valid)` : ""}`} />
              <Stat icon={Gauge} label="Top Speed" value={s.top_speed ? `${s.top_speed} km/h` : "—"} />
              <Stat icon={Trophy} label="Position" value={s.position ? `P${s.position}` : "—"} />
              <Stat icon={Zap} label="Points" value={`+${s.points_awarded || 0}`} color="#ffb020" />
            </>
          )}
        </div>
        {s.status === "active" && (
          <div className="px-5 pb-5">
            <button onClick={onEnd} className="neon-outline-btn w-full py-2.5 text-sm" data-testid={`end-session-${s.id}`}><StopCircle className="w-4 h-4" /> End session</button>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="rounded-xl px-3 py-2.5" style={{ background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.12)" }}>
    <p className="text-slate-500 text-[10px] uppercase tracking-widest flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</p>
    <p className="font-bold mt-0.5" style={{ color: color || "#eaf2ff" }}>{value}</p>
  </div>
);
